import type { SidebarPage, HTTPMethod } from '@readme/backend/iso';

import useClassy from '@core/hooks/useClassy';
import Method from '@ui/API/Method';
import Icon from '@ui/Icon';
import Tooltip from '@ui/Tooltip';

import classes from './style.module.scss';

interface SidebarMetaProps {
  apiMethod?: HTTPMethod;
  deprecated: boolean;
  isAPIReference: boolean;
  isLinkType: boolean;
  openLinkInNewTab: boolean;
  pageType: SidebarPage['type'];
  showMethodInSidebar: boolean;
}

const SidebarMeta = ({
  apiMethod,
  deprecated,
  isLinkType,
  isAPIReference,
  openLinkInNewTab,
  pageType,
  showMethodInSidebar,
}: SidebarMetaProps) => {
  const bem = useClassy(classes, 'Sidebar');

  const showMeta =
    !!deprecated ||
    !!isLinkType ||
    (!!isAPIReference && pageType === 'webhook') ||
    (!!isAPIReference && !!showMethodInSidebar);

  return showMeta ? (
    <span className={bem('-link-meta')}>
      {!!deprecated && (
        <Tooltip
          asTitle
          content={`Deprecated ${pageType === 'endpoint' ? 'Endpoint' : 'Doc'}`}
          interactive={false}
          placement="bottom"
        >
          <button
            aria-label={`Deprecated ${pageType === 'endpoint' ? 'Endpoint' : 'Doc'}`}
            className={bem('-link-deprecated')}
            type="button"
          >
            <Icon aria-hidden="true" name="alert-circle" size="sm" wrapperClassName={bem('-link_deprecated-icon')} />
          </button>
        </Tooltip>
      )}
      {!!isLinkType && (
        <i
          aria-label={openLinkInNewTab ? 'Opens in new tab' : undefined}
          className={bem('-link-icon', '-link-externalIcon', 'icon-arrow-up-right')}
          role="img"
          {...(!openLinkInNewTab && { 'aria-hidden': 'true' })}
        />
      )}
      {!!isAPIReference && (
        <>
          {pageType === 'webhook' && <Icon aria-label="Webhook" name="webhook" size="sm" />}
          {!!showMethodInSidebar && !!apiMethod && (
            <Method className={bem('-method')} fixedWidth type={apiMethod.toLowerCase() as HTTPMethod} />
          )}
        </>
      )}
    </span>
  ) : null;
};

export default SidebarMeta;
